import moment from "moment";
import { Fragment, useEffect, useState } from "react";
import Modal from "../modal";
import Step1 from "./step1";
import Step2 from "./step2";
import DistrictProgramOption from "../../api/models/districtProgramOption";
import EntryState from "./types/entryState";
import DistrictProgramSelectOption from "./types/districtProgramSelectOption";
import { daysOfWeek } from "../../common/utils";
import LogTimeModalProps from "./types/logTimeModal";

/**
 * Processes the district/program options
 * @param districts 
 * @returns 
 */
const getDistrictProgramSelectOptions = (districts: DistrictProgramOption[]) => {
    let options = [] as DistrictProgramSelectOption[];
    districts.forEach((district, i) => options.push({ districtId: district.districtId ?? -1, label: district.name, programId: district.programId ?? -1, payrates: district.payrates ?? []}));
    return options;
}

/**
 * Popup that's used to allow users to enter time for a selected week and discipline.
 * Passing in a selectedTimesheet is required to prevent users from making duplicate entries. 
 */
const LogtimeModal = ({
    open = false,
    startDate,
    endDate,
    disciplineId,
    districts,
    districtProgramOptions,
    selectedTimesheet,
    editEntry,
    toggle,
    timesheetsAsync,
    allTaskTypes = [],
    resetModal = false,
    setResetModal
}: LogTimeModalProps) => {
    //Get the district program options
    const districtProgramSelectOptions = getDistrictProgramSelectOptions(districtProgramOptions);
    const [hasChanges, setHasChanges] = useState<boolean>(false);
    const [openHasChangesModal, setOpenHasChangesModel] = useState<boolean>(false);
    const [districtProgramSelectOption, setDistrictProgramSelectOption] = useState<DistrictProgramSelectOption | null>(null);
    /**
     * Generates the default state for the entry.
     * @return EntryState The default state object.
     */
    const getDefaultEntryState = () => {
        return {
            startDate: startDate ?? moment(),
            endDate: endDate ?? moment(),
            disciplineId: disciplineId,
            districts: districts,
            districtProgramSelectOptions: districtProgramSelectOptions,
            editingEntry: false,
            hasValidationErrors: false,
            keepOpen: true,
            saveButtonDisabled: false,
            timesheetEntryTasks: []
        };
    }

    /**
     * Hooks
     */
    const [step, setStep] = useState<number>(1);
    const [entryState, setEntryModel] = useState<EntryState>({
        startDate: startDate ?? moment(),
        endDate: endDate ?? moment(),
        disciplineId: disciplineId,
        districts: districts,
        districtProgramSelectOptions: districtProgramSelectOptions,
        editingEntry: false,
        hasValidationErrors: false,
        keepOpen: false
    });

    /**
     * Set up our entry state every time certain variables are updated
     * This is essentially the central hub of the component. Everything
     * passes through here.
     */
    useEffect(() => {
        let newEntry: EntryState = getDefaultEntryState();
        newEntry.startDate = startDate ?? moment();
        newEntry.disciplineId = disciplineId;
        newEntry.selectedTimesheet = selectedTimesheet;
        newEntry.entryTypeId = entryState.entryTypeId;
        newEntry.entryTypeName = entryState.entryTypeName;
        newEntry.programId = entryState.programId ?? -1;
        newEntry.programName = entryState.programName?.valueOf() ?? "";
        newEntry.schoolId = entryState.schoolId;
        newEntry.districtId = entryState.districtId;

        //see if we are editing a previous entry
        if (typeof editEntry !== "undefined" && editEntry.task.entries.length > 0) {
            const district = entryState.districts.find(d => d.districtId === editEntry.districtId);
            newEntry.district = district;
            newEntry.districtId = district?.districtId;
            newEntry.existingMinutesEntered = editEntry.entry?.minutes;
            newEntry.dailyAllowance = district?.dailyAllowance ?? 0;
            newEntry.dailyOverageAllowance = district?.dailyOverageAllowance ?? 0;
            newEntry.unlimitedOverageAllowed = district?.unlimitedOverageAllowed ?? false;
            newEntry.programId = editEntry.task.programId ?? -1;
            newEntry.programName = editEntry.task.programName?.valueOf() ?? "";
            newEntry.schoolId = editEntry.schoolId;
            newEntry.entryTypeId = editEntry.task.entryTypeId;
            newEntry.taskTypeId = editEntry.task.taskTypeId;
            newEntry.editingEntry = true;

            //kind of a hack, but this displays on the top of the edit screen
            newEntry.districtName = editEntry.title;
            newEntry.schoolName = editEntry.subTitle;

            //handle for group time vs student entry
            newEntry.selectedStudents = [];
            editEntry.students.forEach((studentId) => newEntry.selectedStudents!.push({ studentId: studentId }));

            //setup entries
            newEntry.timesheetEntries = [];
            daysOfWeek(newEntry.startDate).forEach((day) => {
                var existing = editEntry.task.entries.find(t => moment(t.dateOfEntry).isSame(day));

                newEntry.timesheetEntries!.push({
                    timesheetEntryId: existing?.entryId.valueOf() ?? -1,
                    minutes: existing?.minutes,
                    dateOfEntry: day,
                    notes: existing?.notes?.valueOf() ?? "",
                    enabled: (typeof existing !== "undefined"),
                    recoupment: existing?.recoupment,
                });
            });

            //setup entries
            setStep(2);
        } else {
            newEntry.timesheetEntryTasks = [];
            if (entryState.studentId && entryState.studentId > -1) {
                entryState.studentId = -1;
            }

            if (resetModal) {
                setResetModal && setResetModal(false);
                setStep(1);
            }
        }

        setEntryModel(newEntry);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [disciplineId, startDate, endDate, setEntryModel, selectedTimesheet, editEntry]);


    //data logic
    /**
     * Save the timesheet entries changes to the database. 
     * @param entryState 
     */
    const entryCompleted = async (entryState: EntryState) => {
        toggle(false, true);
        reset();
    }

    /**
     * Navigation logic
     */
    const advanceStep = () => {
        setStep(step + 1);
    }

    /**
     * Show the modal if we have changes. Otherwise, go back
     * @return {void} 
     */
    const goBack = (): void => {
        if (hasChanges) {
            //setHasChanges(false);
            setOpenHasChangesModel(true);
            return;
        }

        doBack();
    }

    /**
     * Performs the actual go back functionality
     * @return {void}
     */
    const doBack = (): void => {
        //Close the modal if it's open
        if (openHasChangesModal) {
            setOpenHasChangesModel(false);
        }

        //if the user goes back then we can clear out the validation errors
        entryState.hasValidationErrors = false;
        entryState.timesheetEntries = [];
        entryState.timesheetEntryTasks = [];
        entryState.taskTypes = [];
        if (step > 1)
            setStep(step - 1);
    }

    /**
     * Resets the navigation
     */
    const reset = () => {
        setStep(1);
        setEntryModel(getDefaultEntryState());
    }

    /**
     * Cancels the entry
     * @param entry 
     */
    const cancel = (entry: EntryState) => {
        if (hasChanges && !entryState.editingEntry) {
            setOpenHasChangesModel(true);
            return;
        }
        toggle(false, false); //if keep open was set to true then entries could have been created
        reset();
    }

    const setEntryType = (entryType: number) => {
        setEntryModel(entryState => {
            entryState.entryTypeId = entryType;
            return entryState;
        });
    }
    const handleDistrictOptionSelected = (option: DistrictProgramSelectOption) => {
        setDistrictProgramSelectOption(option);  // Update the selected district/program option
      };
    return (
        <Fragment>
            {/**
             * Has Changes Modal
             */}
            <Modal
                saveText='Okay'
                open={openHasChangesModal}
                onCancel={() => {
                    setOpenHasChangesModel(false)
                }}
                onSave={() => {
                    setHasChanges(false);
                    doBack();
                }}
                showButtons
            >
                Data has been entered but not saved. Are you sure you would like to continue without saving?<br /><strong>Warning:</strong> All unsaved data will be lost.
            </Modal>

            {/**
             * Logtime Modal
             */}
            <Modal
                saveText='Save'
                open={open}
                onCancel={() => {
                    //ignore the cancel event on this modal, it's handled inside of this component
                }}
                onSave={() => {
                    //toggle(false, false);
                    //reset();
                }}
                showButtons={false}
            >
                <Fragment>
                    {!startDate && <div>No Start Date Supplied, please pick a week first</div>}

                    {startDate &&
                        <Fragment>
                            {step === 1 &&
                                  <Step1 entryState={entryState} completed={(e) => { setEntryModel(e); advanceStep(); }} cancel={cancel} onDistrictSelected={handleDistrictOptionSelected} />
                            }
                            {step === 2 &&
                                 <Step2 entryState={entryState} completed={entryCompleted} goBack={goBack} cancel={cancel} setHasChanges={(setHasChanges)} hasChanges={hasChanges} timesheetsAsync={timesheetsAsync} allTaskTypes={allTaskTypes} toggle={toggle} setStep={setStep} setEntryType={setEntryType} selectedDistrictProgramOption={districtProgramSelectOption}/>
                            }
                        </Fragment>
                    }
                </Fragment>
            </Modal>
        </Fragment>
    )
}

export default LogtimeModal;